import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import {
  Pagination,
  LoadingState,
  EmptyState,
  ExportButton,
  Breadcrumbs,
  BreadcrumbItem,
} from '../components/ui';
import type { SupportTicket } from '../types';
import { LifeBuoy, Search, X, Clock, Building2, ArrowRight, Filter } from 'lucide-react';

export const SupportTickets: React.FC = () => {
  const getStatusLabelPT = (status: string) => {
    const labels: Record<string, string> = {
      all: 'Todos',
      open: 'Aberto',
      in_progress: 'Em Progresso',
      waiting_for_customer: 'Aguardando Cliente',
      resolved: 'Resolvido',
      closed: 'Fechado',
    };
    return labels[status] || status;
  };

  const getPriorityLabelPT = (priority: string) => {
    const labels: Record<string, string> = {
      critical: 'Crítico',
      high: 'Alto',
      medium: 'Médio',
      low: 'Baixo',
    };
    return labels[priority] || priority;
  };

  const getCategoryLabelPT = (category: string) => {
    const labels: Record<string, string> = {
      technical: 'Técnico',
      billing: 'Faturação',
      general: 'Geral',
    };
    return labels[category] || category;
  };

  const [filters, setFilters] = useState({
    status: 'open',
    priority: '',
    category: '',
    search: '',
  });

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setOffset(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    setOffset(0);
  }, [filters.status, filters.priority, filters.category, limit]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'support-tickets',
      filters.status,
      filters.priority,
      filters.category,
      debouncedSearch,
      offset,
      limit,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.category) params.append('category', filters.category);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await api.get<{ data: SupportTicket[]; meta: any }>(
        `/admin/support/tickets?${params.toString()}`
      );
      return response.data;
    },
  });

  const tickets = data?.data || [];
  const total = data?.meta?.total || 0;

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setOffset(0);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return 'badge-modern badge-danger';
      case 'in_progress':
        return 'badge-modern badge-warning';
      case 'waiting_for_customer':
        return 'badge-modern badge-primary';
      case 'resolved':
      case 'closed':
        return 'badge-modern badge-success';
      default:
        return 'badge-modern badge-secondary';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'badge-modern badge-danger font-bold';
      case 'high':
        return 'badge-modern badge-warning font-semibold';
      case 'medium':
        return 'badge-modern badge-primary';
      case 'low':
      default:
        return 'badge-modern badge-secondary';
    }
  };

  const exportHeaders = [
    { key: 'ticket_number', label: 'Ticket #' },
    { key: 'subject', label: 'Assunto' },
    { key: 'category', label: 'Categoria' },
    { key: 'priority', label: 'Prioridade' },
    { key: 'status', label: 'Estado' },
    { key: 'client_name', label: 'Empresa' },
    { key: 'created_at', label: 'Criado em' },
  ];

  const exportData = tickets.map((t) => ({
    ...t,
    client_name: t.client?.company_name || 'Desconhecida',
  }));

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Tickets de Suporte</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <LifeBuoy className="w-8 h-8 text-primary-500" />
            <span>Tickets de Suporte</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Gira os chamados de suporte técnico, dúvidas e incidentes de faturação dos clientes
          </p>
        </div>
        <ExportButton
          data={exportData}
          filename={`tickets_${Date.now()}.csv`}
          headers={exportHeaders}
        />
      </div>

      {/* Modern Filter Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          {/* Segmented Status Filter */}
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold overflow-x-auto">
            {['all', 'open', 'in_progress', 'waiting_for_customer', 'resolved'].map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => handleFilterChange('status', status)}
                className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all ${
                  filters.status === status
                    ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-2xs font-bold'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {getStatusLabelPT(status)}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="flex-1 lg:max-w-xs relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Pesquisar por assunto ou #"
              className="w-full pl-10 pr-9 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => handleFilterChange('search', '')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                title="Limpar pesquisa"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Priority and Category Selectors */}
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs">
          <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 font-medium">
            <Filter className="w-3.5 h-3.5" />
            <span>Filtros adicionais:</span>
          </div>

          <select
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-1.5 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
          >
            <option value="">Todas as Prioridades</option>
            <option value="critical">Crítica</option>
            <option value="high">Alta</option>
            <option value="medium">Média</option>
            <option value="low">Baixa</option>
          </select>

          <select
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-1.5 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <option value="">Todas as Categorias</option>
            <option value="technical">Técnico</option>
            <option value="billing">Faturação</option>
            <option value="general">Geral</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      {isLoading ? (
        <LoadingState type="card" count={3} />
      ) : tickets.length > 0 ? (
        <>
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <Link key={ticket.id} to={`/support/${ticket.id}`} className="block group">
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-5 hover:shadow-md hover:border-primary-500/40 transition-all">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-mono text-xs font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-lg">
                          #{ticket.ticket_number}
                        </span>
                        <span className={getStatusBadge(ticket.status)}>
                          {getStatusLabelPT(ticket.status)}
                        </span>
                        <span className={getPriorityBadge(ticket.priority)}>
                          {getPriorityLabelPT(ticket.priority)}
                        </span>
                        <span className="badge-modern badge-secondary text-xs">
                          {getCategoryLabelPT(ticket.category)}
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors mb-1">
                        {ticket.subject}
                      </h3>

                      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mt-1">
                        <span className="flex items-center gap-1 font-medium">
                          <Building2 className="w-3.5 h-3.5 text-slate-400" />
                          {ticket.client?.company_name || 'Cliente'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          {new Date(ticket.created_at).toLocaleDateString('pt-PT', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400 group-hover:text-primary-700 dark:group-hover:text-primary-300 shrink-0">
                      <span>Responder</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <Pagination
            total={total}
            limit={limit}
            offset={offset}
            onPageChange={setOffset}
            onLimitChange={setLimit}
          />
        </>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-8">
          <EmptyState
            title="Nenhum ticket encontrado"
            description="Não existem tickets correspondentes aos critérios de pesquisa selecionados."
          />
        </div>
      )}
    </div>
  );
};
